/**
 * A component for collecting, uploading, and managing multiple document collections.
 * Supports multiple profiles with their own document types in a single form.
 */
import React, { useMemo, useCallback } from "react";
import { Button, Flex, Text } from "@jsq/ds/core";
import { useFormContext, Form } from "@jsq/ds/forms";
import { Sizes } from "@jsq/ds/styles";
import { DocumentFormValues } from "../../../hooks/documents/useDocumentProcessor";
import useUnsavedChangesTracker from "../../../hooks/documents/useUnsavedChangesTracker";
import { SingleFileUpload } from "../../common/SingleFileUpload";
import { DocumentData } from "../DocumentCollector/types";
import { prepareFilesToUpload } from "../DocumentCollector/utils";
import { collectionContainer } from "./style_vanilla.css";
import { MultiDocumentCollectorProps, DocumentCollection } from "./types";

// Supported file formats that can be uploaded through the document collector
const SUPPORTED_FORMATS = ["image/*", ".pdf", ".doc", ".docx", ".xlsx", ".xls", ".csv", ".zip"];

/**
 * Renders the form fields for a single document collection
 */
const DocumentCollectionForm: React.FC<{
  collection: DocumentCollection;
  fieldIndexOffset: number;
}> = ({ collection, fieldIndexOffset }) => {
  const formContext = useFormContext<{ documents: DocumentData[] }>();

  return (
    <Flex flexDirection="column" gap={Sizes.MD}>
      {collection.title && (
        <Flex flexDirection="column" gap={Sizes.XS}>
          <Text variant="h4">{collection.title}</Text>
        </Flex>
      )}
      {collection.documentDetails.map((document, index) => {
        const fieldIndex = fieldIndexOffset + index;

        return (
          <Flex key={index} flexDirection="column">
            <SingleFileUpload
              title={document.title}
              description={document.description}
              initialFile={document.file}
              acceptedFileFormats={SUPPORTED_FORMATS}
              onFileSelected={(file) => {
                void formContext.setFieldValue(`documents[${fieldIndex}].file`, file);
                return;
              }}
              onFileDeleted={() => {
                void formContext.setFieldValue(`documents[${fieldIndex}].file`, null);
                return;
              }}
            />
          </Flex>
        );
      })}
    </Flex>
  );
};

/**
 * Component that handles unsaved changes tracking within the form context
 */
const MultiDocumentFormContent: React.FC<{
  documentCollections: DocumentCollection[];
  collectionOffsets: number[];
  initialDocuments: DocumentData[];
  onNavigateBack?: () => void;
  isSubmitting: boolean;
  onUnsavedChangesChange?: (hasChanges: boolean) => void;
}> = ({
  documentCollections,
  collectionOffsets,
  initialDocuments,
  onNavigateBack,
  isSubmitting,
  onUnsavedChangesChange,
}) => {
  // Track unsaved changes and notify parent component
  useUnsavedChangesTracker(initialDocuments, onUnsavedChangesChange);

  return (
    <Flex flexDirection="column" gap={Sizes.LG}>
      {documentCollections.map((collection, index) => (
        <div key={index} className={collectionContainer}>
          <DocumentCollectionForm
            collection={collection}
            fieldIndexOffset={collectionOffsets[index]}
          />
        </div>
      ))}

      <Flex flexDirection="row" gap="10px" justifyContent="flex-end">
        {onNavigateBack && (
          <Button variant="neutral" onClick={onNavigateBack} disabled={isSubmitting}>
            Back
          </Button>
        )}
        <Button data-testid="complete-button" type="submit" isLoading={isSubmitting}>
          Complete
        </Button>
      </Flex>
    </Flex>
  );
};

/**
 * Main component for collecting multiple document collections with support for uploading, replacing,
 * and deleting documents across multiple profiles.
 */
export const MultiDocumentCollector: React.FC<MultiDocumentCollectorProps> = ({
  documentCollections,
  onNavigateBack,
  onComplete,
  isSubmitting,
  onUnsavedChangesChange,
}) => {
  // Flatten all documents from all collections into a single array for the form
  const initialDocuments = useMemo<Array<DocumentData>>(() => {
    return documentCollections.flatMap((collection, collectionIndex) =>
      collection.documentDetails.map((detail, detailIndex) => ({
        id: detail.file?.uuid ? Number(detail.file.uuid) : undefined,
        file: detail.file,
        baseType: detail.baseType,
        subType: detail.subType,
        // Store metadata for processing
        meta: {
          profileId: collection.profileId,
          collectionIndex,
          detailIndex,
        },
      })),
    );
  }, [documentCollections]);

  // Calculate field index offsets for each collection
  const collectionOffsets = useMemo(() => {
    const offsets: number[] = [];
    let currentOffset = 0;

    documentCollections.forEach((collection) => {
      offsets.push(currentOffset);
      currentOffset += collection.documentDetails.length;
    });

    return offsets;
  }, [documentCollections]);

  const uploadHandler = useCallback(
    (newSelectedFiles: DocumentFormValues) => {
      const { newFiles, deleteDocIds } = prepareFilesToUpload(newSelectedFiles);
      onComplete(newFiles, deleteDocIds);
    },
    [onComplete],
  );

  /**
   * Process documents for all collections
   */
  const handleSubmit = useCallback(
    (formValues: { documents: DocumentData[] }) => {
      uploadHandler(formValues);
    },
    [uploadHandler],
  );

  return (
    <div>
      <Form<{ documents: DocumentData[] }>
        initialValues={{
          documents: initialDocuments,
        }}
        submitOnlyChangedValues={false}
        onSubmit={handleSubmit}
      >
        <MultiDocumentFormContent
          documentCollections={documentCollections}
          collectionOffsets={collectionOffsets}
          initialDocuments={initialDocuments}
          onNavigateBack={onNavigateBack}
          isSubmitting={isSubmitting}
          onUnsavedChangesChange={onUnsavedChangesChange}
        />
      </Form>
    </div>
  );
};
